import React, { useState, useEffect } from "react";
import {
  Table,
  Button,
  Checkbox,
  Space,
  message,
  Select,
  Tag,
  Empty,
  Row,
  Col,
  InputNumber,
} from "antd";
import {
  CheckOutlined,
  FileTextOutlined,
  DownOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { WorkRequestLine, WorkBoqLine } from "../types";
import workBoqApis from "../../../api/WorkOrderManagement/WorkBOQ";

const { Option } = Select;

interface WorkBoqLineSelectorProps {
  onLinesSelected: (lines: WorkRequestLine[]) => void;
  selectedLines?: WorkRequestLine[];
  editingLine?: WorkRequestLine | null;
  siteId?: string;
}

const WorkBoqLineSelector: React.FC<WorkBoqLineSelectorProps> = ({
  onLinesSelected,
  selectedLines = [],
  editingLine,
  siteId,
}: any) => {
  const [workBoqLines, setWorkBoqLines] = useState<WorkBoqLine[]>([]);
  const [selectedLineIds, setSelectedLineIds] = useState<string[]>([]);
  const [lineQuantities, setLineQuantities] = useState<{
    [key: string]: number;
  }>({});
  const [loading, setLoading] = useState(false);
  const [workBoqs, setWorkBoqs] = useState<any[]>([]);
  const [allCategories, setAllCategories] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [selectedWorkBoqId, setSelectedWorkBoqId] = useState<string>("");
  const [selectedCategoryId, setSelectedCategoryId] = useState<string>("");

  useEffect(() => {
    if (siteId) {
      fetchWorkBoqs(siteId);
    }
  }, [siteId]);

  useEffect(() => {
    // Initialize selected lines if editing
    if (editingLine && editingLine.sourceId) {
      setSelectedLineIds([editingLine.sourceId]);
      setLineQuantities({
        [editingLine.sourceId]:
          (editingLine as any).requestedQuantity || editingLine.quantity,
      });

      // Initialize Work BOQ and Category for edit mode
      const masterData: any = fetchWorkBoqs(siteId);

      // First, try to find all work BOQ lines to locate which BOQ contains our line
      let foundBoqId = null;
      let foundCategoryId = null;

      // Check all BOQs to find the one containing this line ID
      // for (const boq of masterData.workBoqs) {
      //   try {
      //     const allLinesInBoq = getWorkBoqLines(boq.id); // Get all lines without category filter
      //     const lineExists = allLinesInBoq.some(line => line.lineId === editingLine.sourceId);
      //     if (lineExists) {
      //       foundBoqId = boq.id;
      //       break;
      //     }
      //   } catch (error) {
      //     console.log('Error checking BOQ:', boq.id);
      //   }
      // }

      // Find category from the editing line
      const category = masterData.categories.find(
        (cat: any) => cat.name === editingLine.category
      );
      if (category) {
        foundCategoryId = category.id;
      }

      if (foundBoqId) {
        setSelectedWorkBoqId(foundBoqId);
        if (foundCategoryId) {
          setSelectedCategoryId(foundCategoryId);
          fetchWorkBoqLines(foundBoqId, foundCategoryId);
        } else {
          fetchWorkBoqLines(foundBoqId);
        }
      } else {
        // Fallback: use first BOQ if we can't find the specific one
        console.warn("Could not find BOQ for line ID:", editingLine.sourceId);
        const firstBoq = masterData.workBoqs[0];
        if (firstBoq) {
          setSelectedWorkBoqId(firstBoq.id);
          if (foundCategoryId) {
            setSelectedCategoryId(foundCategoryId);
            fetchWorkBoqLines(firstBoq.id, foundCategoryId);
          } else {
            fetchWorkBoqLines(firstBoq.id);
          }
        }
      }
    } else if (selectedLines.length > 0) {
      // const lineIds = selectedLines
      //   .filter((line) => line.sourceId)
      //   .map((line) => line.sourceId!);
      // setSelectedLineIds(lineIds);
    }
  }, [selectedLines, editingLine]);

  const fetchWorkBoqs = async (siteId: string) => {
    try {
      const { data, status }: any =
        await workBoqApis.workBoqDropdownAlongWithCategory(parseInt(siteId),"Work Request");
      if (status && data && Array.isArray(data)) {
        setWorkBoqs(data);

        // Extract all unique categories from all BOQs
        const allCategoriesSet = new Set();
        data.forEach((boq: any) => {
          if (boq.workCategories && Array.isArray(boq.workCategories)) {
            boq.workCategories.forEach((category: any) => {
              allCategoriesSet.add(JSON.stringify(category));
            });
          }
        });

        const allCategories = Array.from(allCategoriesSet).map((catStr: any) =>
          JSON.parse(catStr)
        );
        setAllCategories(allCategories);
        setCategories(allCategories);
      }
    } catch (error) {
      console.log("Error fetching work BOQs:", error);
      message.error("Failed to fetch work BOQs");
    }
  };

  const fetchWorkBoqLines = async (workBoqId: string, categoryId?: string) => {
    try {
      setLoading(true);

      let ApiEndPointCall;

      if (categoryId) {
        ApiEndPointCall = workBoqApis.getWorkBoqByIdWithCategory(
          workBoqId,
          categoryId
        );
      } else {
        ApiEndPointCall = workBoqApis.getWorkBoqById(workBoqId);
      }

      const { data, status }: any = await ApiEndPointCall;

      if (status && data && data.workBoq && data.workBoq.workBoqLines) {
        let lines = data.workBoq.workBoqLines;

        // Filter by category if specified
        if (categoryId) {
          lines = lines.filter(
            (line: any) => line.workCategoryId?.toString() === categoryId
          );
        }

        // Transform API response to WorkBoqLine format
        const transformedLines: WorkBoqLine[] = lines.map((line: any) => ({
          lineId: line.workBoqLineId?.toString() || line.id?.toString(),
          taskName: line.taskTitle || line.taskName,
          category: line.workCategoryName || line.category,
          workCategoryId: line.workCategoryId,
          subcategory: line.workSubCategoryName || line.subcategory,
          quantity: line.quantity || 0,
          allocatedQty: line.requestedQuantity || 0,
          pendingQty: line.quantityAvailableForWorkRequest || 0,
          uom: line.uomName,
          uomId: line.uomId,
          towerFlatInfo:
            `${line.towerVilla || ""} ${line.floorName || ""} ${
              line.flatName || ""
            }`.trim() || "-",
          documents: line.dms,
          // Add material allocation data
          materialAllocation: line.workBoqLineMaterials || [],
        }));

        // In edit mode, show only the edited line
        if (editingLine && editingLine.sourceId) {
          const filteredLines = transformedLines.filter(
            (line) => line.lineId === editingLine.sourceId
          );
          setWorkBoqLines(filteredLines);
        } else {
          setWorkBoqLines(transformedLines);
        }
      } else {
        setWorkBoqLines([]);
      }
    } catch (error) {
      console.log("Error fetching work BOQ lines:", error);
      message.error("Failed to fetch work BOQ lines");
      setWorkBoqLines([]);
    } finally {
      setLoading(false);
    }
  };

  const handleWorkBoqChange = (workBoqId: string) => {
    // In edit mode, don't allow BOQ changes
    if (editingLine) {
      message.warning("Work BOQ cannot be changed in edit mode");
      return;
    }

    setSelectedWorkBoqId(workBoqId);
    setSelectedLineIds([]); // Clear selections when BOQ changes
    setSelectedCategoryId(""); // Clear category filter

    // Filter categories based on selected Work BOQ
    if (workBoqId) {
      const selectedBoq = workBoqs.find(
        (boq) => boq.workBoqId?.toString() === workBoqId
      );
      if (selectedBoq && selectedBoq.workCategories) {
        // Set categories from the selected BOQ
        setCategories(selectedBoq.workCategories);
      } else {
        // Fallback to all categories if BOQ doesn't have specific categories
        setCategories(allCategories);
      }
      fetchWorkBoqLines(workBoqId);
    } else {
      setWorkBoqLines([]);
      setCategories(allCategories); // Reset to all categories
    }
  };

  const handleCategoryChange = (categoryId: string) => {
    // In edit mode, don't allow category changes
    if (editingLine) {
      message.warning("Category cannot be changed in edit mode");
      return;
    }

    setSelectedCategoryId(categoryId);
    setSelectedLineIds([]); // Clear selections when category changes
    if (selectedWorkBoqId) {
      fetchWorkBoqLines(selectedWorkBoqId, categoryId);
    }
  };

  const handleLineSelection = (lineId: string, checked: boolean) => {
    if (checked) {
      setSelectedLineIds([...selectedLineIds, lineId]);
      // Initialize with available quantity if not already set
      const line: any = workBoqLines.find((l) => l.lineId === lineId);
      if (line && !lineQuantities[lineId]) {
        setLineQuantities((prev) => ({ ...prev, [lineId]: line.pendingQty }));
      }
    } else {
      setSelectedLineIds(selectedLineIds.filter((id) => id !== lineId));
      // Remove quantity when deselected
      setLineQuantities((prev) => {
        const updated = { ...prev };
        delete updated[lineId];
        return updated;
      });
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allLineIds = workBoqLines.map((line) => line.lineId);
      setSelectedLineIds(allLineIds);
      // Initialize quantities for all lines
      const quantities: { [key: string]: number } = {};
      workBoqLines.forEach((line) => {
        quantities[line.lineId] = lineQuantities[line.lineId] || line.quantity;
      });
      setLineQuantities(quantities);
    } else {
      setSelectedLineIds([]);
      setLineQuantities({});
    }
  };

  const handleQuantityChange = (lineId: string, value: number | null) => {
    if (value && value > 0) {
      const line = workBoqLines.find((l) => l.lineId === lineId);
      if (line && value <= line.quantity) {
        setLineQuantities((prev) => ({ ...prev, [lineId]: value }));
      } else {
        message.error(
          `Requested quantity cannot exceed available quantity (${line?.quantity})`
        );
      }
    }
  };

  const handleAddSelected = () => {
    if (selectedLineIds.length === 0) {
      message.warning("Please select at least one work line");
      return;
    }

    // Validate quantities
    const invalidLines = selectedLineIds.filter((lineId) => {
      const requestedQty = lineQuantities[lineId];
      return !requestedQty || requestedQty <= 0;
    });

    if (invalidLines.length > 0) {
      message.error(
        "Please specify valid quantities for all selected work lines"
      );
      return;
    }

    const selectedLineObjects = workBoqLines.filter((line) =>
      selectedLineIds.includes(line.lineId)
    );

    console.log("selectedLineObjects", selectedLineObjects);

    // Convert WorkBoqLine to WorkRequestLine format
    const workRequestLines: any[] = selectedLineObjects.map((line: any) => ({
      id: `${Date.now()}-${Math.random()}`,
      taskTitle: line.taskName,
      taskDescription: `BOQ Line: ${line.lineId} - ${line.taskName}`,
      category: line.category,
      subcategory: line.subcategory,
      quantity: lineQuantities[line.lineId] || line.quantity,
      availableQuantity: line.quantity,
      requestedQuantity: lineQuantities[line.lineId] || line.quantity,
      pendingQty: line.pendingQty,
      allocatedQty: line.allocatedQty,
      uom: line.uom,
      source: "Work BOQ",
      sourceId: line.lineId,
      assignedTower: line.towerFlatInfo?.split(" / ")[0] || undefined,
      assignedVilla: line.towerFlatInfo?.includes("Villa")
        ? line.towerFlatInfo
        : undefined,
      documents: line.documents || [],
      materialAllocation: line.materialAllocation,
      // Include IDs for API payload
      workCategoryId: line.workCategoryId,
      workSubCategoryId: line.workSubCategoryId,
      uomId: line.uomId,
    }));

    onLinesSelected(workRequestLines);
  };

  // Material BOQ expandable row component
  const expandedRowRender = (record: WorkBoqLine) => {
    const materialAllocation = (record as any).materialAllocation;

    if (!materialAllocation || materialAllocation.length === 0) {
      return null;
    }

    const materialColumns = [
      {
        title: "Material Name",
        dataIndex: "materialName",
        key: "materialName",
        width: "40%",
        render: (name: string, material: any) => (
          <div>
            <div className="fw-bold">{name}</div>
            {material.categoryName && (
              <Tag color="blue" style={{ marginTop: 4 }}>
                {material.categoryName}
              </Tag>
            )}
          </div>
        ),
      },
      {
        title: "Allocated Qty",
        dataIndex: "allocatedQty",
        key: "allocatedQty",
        width: "20%",
        align: "right" as const,
        render: (qty: number) => <strong>{qty?.toLocaleString()}</strong>,
      },
      {
        title: "UOM",
        dataIndex: "uomName",
        key: "uomName",
        width: "20%",
        align: "center" as const,
        render: (uom: string) => <Tag color="geekblue">{uom}</Tag>,
      },
      // {
      //   title: "Material ID",
      //   dataIndex: "materialId",
      //   key: "materialId",
      //   width: "20%",
      //   render: (id: number) => <code className="text-muted">{id}</code>,
      // },
    ];

    return (
      <div
        style={{
          margin: "16px 0",
          backgroundColor: "#fafafa",
          padding: "16px",
          borderRadius: "6px",
        }}
      >
        <div className="mb-2">
          <strong>
            {" "}
            Material Allocation ({materialAllocation.length} items)
          </strong>
        </div>
        <Table
          columns={materialColumns}
          dataSource={materialAllocation}
          pagination={false}
          size="small"
          rowKey="workBoqLineMetId"
          bordered
        />
      </div>
    );
  };

  // FSD Table Format: Select, Line ID, Category, Subcategory, Task Name, Quantity, UOM, Tower/Flat Info, Documents
  const columns = [
    {
      title: "",
      key: "select",
      width: 50,
      render: (_: any, record: WorkBoqLine) => {
        const materialAllocation = (record as any).materialAllocation;
        const hasMaterialBOQ =
          materialAllocation && materialAllocation.length > 0;

        return (
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <Checkbox
              checked={selectedLineIds.includes(record.lineId)}
              onChange={(e) =>
                handleLineSelection(record.lineId, e.target.checked)
              }
              disabled={editingLine ? true : false}
            />
            {/* {hasMaterialBOQ && (
              <Tag color="purple" style={{ fontSize: '10px', marginLeft: '4px' }}>📦</Tag>
            )} */}
          </div>
        );
      },
    },
    // {
    //   title: "Line ID",
    //   dataIndex: "lineId",
    //   key: "lineId",
    //   width: 100,
    //   render: (text: string) => <strong>{text}</strong>,
    // },
    {
      title: "Category",
      dataIndex: "category",
      key: "category",
      width: 120,
      render: (text: string) => <Tag color="blue">{text}</Tag>,
    },
    // {
    //   title: "Subcategory",
    //   dataIndex: "subcategory",
    //   key: "subcategory",
    //   width: 120,
    //   render: (text: string) => text || "-",
    // },
    {
      title: "Task Name",
      dataIndex: "taskName",
      key: "taskName",
      width: 200,
      render: (text: string) => <div className="fw-bold">{text}</div>,
    },
    {
      title: "BOQ Qty",
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Allocated Qty",
      dataIndex: "allocatedQty",
      key: "allocatedQty",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Pending Qty",
      dataIndex: "pendingQty",
      key: "pendingQty",
      width: 100,
      align: "right" as const,
      render: (quantity: number) => (
        <strong>{quantity?.toLocaleString()}</strong>
      ),
    },
    {
      title: "Qty to Allocate",
      key: "requestedQty",
      width: 130,
      align: "center" as const,
      render: (_: any, record: any) => {
        const isSelected = selectedLineIds.includes(record.lineId);
        const currentValue = lineQuantities[record.lineId] || record.pendingQty;

        return isSelected ? (
          <InputNumber
            min={1}
            max={record.pendingQty}
            value={currentValue}
            onChange={(value) => handleQuantityChange(record.lineId, value)}
            style={{ width: "100%" }}
            size="small"
          />
        ) : (
          <span className="text-muted">Select first</span>
        );
      },
    },
    {
      title: "UOM",
      dataIndex: "uom",
      key: "uom",
      width: 80,
      align: "center" as const,
    },
    {
      title: "Tower/Flat Info",
      dataIndex: "towerFlatInfo",
      key: "towerFlatInfo",
      width: 150,
      render: (text: string) => (
        <div style={{ wordWrap: "break-word", wordBreak: "break-word" }}>
          {text || "-"}
        </div>
      ),
    },
    // {
    //   title: "Documents",
    //   dataIndex: "documents",
    //   key: "documents",
    //   width: 100,
    //   align: "center" as const,
    //   render: (docs: string[]) => (
    //     <div>
    //       {docs && docs.length > 0 ? (
    //         <Button type="link" size="small" icon={<FileTextOutlined />}>
    //           View ({docs.length})
    //         </Button>
    //       ) : (
    //         <span className="text-muted">-</span>
    //       )}
    //     </div>
    //   ),
    // },
  ];

  const isAllSelected =
    workBoqLines.length > 0 && selectedLineIds.length === workBoqLines.length;
  const isIndeterminate =
    selectedLineIds.length > 0 && selectedLineIds.length < workBoqLines.length;

  return (
    <div>
      {/* <div className="mb-3">
        <h6>📋 Select Work Lines from BOQ</h6>
        <p className="text-muted">
          Choose work lines from existing approved BOQs
        </p>
      </div> */}

      {/* FSD Input Form Specification */}
      <Row className="gy-3 mb-3">
        {/* Work BOQ ID - Mandatory */}
        <Col span={12}>
          <label className="form-label">
            Work BOQ ID <span className="text-danger">*</span>
          </label>
          <Select
            placeholder="List of existing Work BOQs"
            style={{ width: "100%" }}
            value={selectedWorkBoqId}
            onChange={handleWorkBoqChange}
            disabled={editingLine ? true : false}
          >
            {workBoqs.map((boq) => (
              <Option key={boq.workBoqId} value={boq.workBoqId?.toString()}>
                <div>
                  <div className="fw-bold">{boq.boqTitle}</div>
                  {/* <small className="text-muted">{boq.siteName} - {boq.workBoqSequenceId}</small> */}
                </div>
              </Option>
            ))}
          </Select>
          {/* <div className="form-note text-muted mt-1">
            FSD: List of existing Work BOQs
          </div> */}
        </Col>

        {/* Work Category - Mandatory */}
        <Col span={12}>
          <label className="form-label">
            Work Category <span className="text-danger">*</span>
          </label>
          <Select
            placeholder="Work Categories from selected Work BOQ"
            style={{ width: "100%" }}
            value={selectedCategoryId}
            onChange={handleCategoryChange}
            disabled={editingLine ? true : !selectedWorkBoqId}
            allowClear
          >
            {categories.map((category) => (
              <Option
                key={category.workCategoryId}
                value={category.workCategoryId?.toString()}
              >
                {category.workCategoryName}
              </Option>
            ))}
          </Select>
          {/* <div className="form-note text-muted mt-1">
            FSD: Work Categories from selected Work BOQ
          </div> */}
        </Col>
      </Row>

      {/* BOQ Work Line Selection Table - FSD Display Format */}
      {(selectedWorkBoqId || editingLine) && (
        <div className="mb-3">
          <div className="d-flex justify-content-between align-items-center mb-2">
            {!editingLine && (
              <>
                <div>
                  <Checkbox
                    indeterminate={isIndeterminate}
                    checked={isAllSelected}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                    disabled={workBoqLines.length === 0}
                  >
                    Select All ({workBoqLines.length} work lines)
                  </Checkbox>
                </div>
                <div>
                  <span className="text-muted small">
                    {selectedLineIds.length} of {workBoqLines.length} work lines
                    selected
                  </span>
                </div>
              </>
            )}
            {editingLine && (
              <div className="w-100">
                <div className="alert alert-info mb-0">
                  <strong>Edit Mode:</strong> Editing {editingLine.taskTitle} -
                  Only quantity can be modified
                </div>
              </div>
            )}
          </div>

          <Table
            className="customTable"
            columns={columns}
            dataSource={workBoqLines}
            loading={loading}
            pagination={false}
            scroll={{ x: "max-content" }}
            size="small"
            bordered
            rowKey="lineId"
            expandable={{
              expandedRowRender,
              expandIcon: ({ expanded, onExpand, record }) => {
                const materialAllocation = (record as any).materialAllocation;
                const hasMaterialBOQ =
                  materialAllocation && materialAllocation.length > 0;

                if (!hasMaterialBOQ) return null;

                return expanded ? (
                  <DownOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{ color: "#1890ff", cursor: "pointer" }}
                  />
                ) : (
                  <RightOutlined
                    onClick={(e) => onExpand(record, e)}
                    style={{ color: "#1890ff", cursor: "pointer" }}
                  />
                );
              },
              rowExpandable: (record) => {
                const materialAllocation = (record as any).materialAllocation;
                return materialAllocation && materialAllocation.length > 0;
              },
            }}
            locale={{
              emptyText: "No work lines found for this BOQ and category",
            }}
          />
        </div>
      )}

      {!selectedWorkBoqId && (
        <Empty
          description="Please select a Work BOQ to view available work lines"
          image={Empty.PRESENTED_IMAGE_SIMPLE}
        />
      )}

      {/* Action Buttons */}
      <div className="d-flex justify-content-end">
        <Space>
          <Button
            type="primary"
            icon={<CheckOutlined />}
            onClick={handleAddSelected}
            disabled={selectedLineIds.length === 0}
          >
            Add Selected Lines ({selectedLineIds.length})
          </Button>
        </Space>
      </div>

      {/* FSD Note */}
      {/* <div className="mt-3 p-2 bg-light rounded">
        <small className="text-muted">
          <strong>FSD Reference:</strong> Display BOQ Work Line Selection Table.
          Table format: Select, Line ID, Category, Subcategory, Task Name, Quantity, UOM, Tower/Flat Info, Documents
        </small>
      </div> */}
    </div>
  );
};

export default WorkBoqLineSelector;
